import { useTranslation } from 'react-i18next';
import type { CalendarEntry, GlobalEvent, SchoolHoliday, PersonalTask } from '@/mocks/calendarEntries';

interface CalendarListProps {
  entries: CalendarEntry[];
  visibleTypes: { global: boolean; holiday: boolean; task: boolean };
  selectedDate: string | null;
  onTaskToggle: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
}

function getTypeIcon(type: string): string {
  switch (type) {
    case 'global': return 'ri-global-line';
    case 'holiday': return 'ri-calendar-close-line';
    case 'task': return 'ri-task-line';
    default: return 'ri-calendar-event-line';
  }
}

function getTypeColor(type: string): string {
  switch (type) {
    case 'global': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'holiday': return 'bg-red-100 text-red-700 border-red-200';
    case 'task': return 'bg-green-100 text-green-700 border-green-200';
    default: return 'bg-background-100 text-foreground-600 border-background-200';
  }
}

function getTypeLabel(type: string, t: (key: string) => string): string {
  switch (type) {
    case 'global': return t('dashboard.globalEvents');
    case 'holiday': return t('dashboard.schoolHolidays');
    case 'task': return t('dashboard.personalTasks');
    default: return type;
  }
}

export default function CalendarList({ entries, visibleTypes, selectedDate, onTaskToggle, onDeleteTask }: CalendarListProps) {
  const { t } = useTranslation();

  const filtered = entries.filter((e) => {
    if (e.type === 'global') return visibleTypes.global;
    if (e.type === 'holiday') return visibleTypes.holiday;
    if (e.type === 'task') return visibleTypes.task;
    return true;
  });

  // Extract DD/MM from date comparison
  const matchesFilter = (entry: CalendarEntry, selDate: string | null): boolean => {
    if (!selDate) return true;
    const parts = selDate.split('-');
    const dayMonth = `${parts[2]}/${parts[1]}`;
    if (entry.type === 'global') return (entry as GlobalEvent).date === dayMonth;
    if (entry.type === 'holiday') {
      const h = entry as SchoolHoliday;
      return selDate >= h.startDate && selDate <= h.endDate;
    }
    if (entry.type === 'task') return entry.date === selDate;
    return true;
  };

  const dateFiltered = selectedDate ? filtered.filter((e) => matchesFilter(e, selectedDate)) : filtered;

  // Sort: upcoming first
  const sorted = [...dateFiltered].sort((a, b) => {
    const getSortDate = (entry: CalendarEntry): string => {
      if (entry.type === 'global') {
        const parts = (entry as GlobalEvent).date.split('/');
        return `2026-${parts[1]}-${parts[0]}`;
      }
      if (entry.type === 'holiday') return (entry as SchoolHoliday).startDate;
      return entry.date;
    };
    return getSortDate(a).localeCompare(getSortDate(b));
  });

  if (sorted.length === 0) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-12 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
          <i className="ri-calendar-2-line text-2xl text-foreground-300" />
        </div>
        <h3 className="font-heading font-700 text-foreground-700 text-base mb-1">{t('dashboard.noEventsFound')}</h3>
        <p className="text-foreground-400 text-sm font-label">{t('common.tryAdjustingFilters')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((entry) => {
        const isTaskEntry = entry.type === 'task';
        const task = isTaskEntry ? (entry as PersonalTask) : null;

        return (
          <div
            key={entry.id}
            className={`bg-background-50 rounded-xl border border-background-200/70 p-4 flex items-start gap-4 transition-colors hover:border-background-300/60 ${
              isTaskEntry && task?.completed ? 'opacity-60' : ''
            }`}
          >
            {/* Type icon badge */}
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${getTypeColor(entry.type)}`}>
              <i className={`${getTypeIcon(entry.type)} text-base`} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className={`font-label font-semibold text-foreground-900 text-sm ${isTaskEntry && task?.completed ? 'line-through' : ''}`}>
                  {entry.type === 'global' ? (entry as GlobalEvent).eventName :
                   entry.type === 'holiday' ? (entry as SchoolHoliday).holidayTitle :
                   (entry as PersonalTask).title}
                </h4>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-label font-medium ${getTypeColor(entry.type)}`}>
                  {getTypeLabel(entry.type, t)}
                </span>
                {entry.type === 'holiday' && (entry as SchoolHoliday).impactsAttendance && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-label font-medium bg-amber-100 text-amber-700">
                    {t('dashboard.impactsAttendance')}
                  </span>
                )}
              </div>

              {/* Date info */}
              <div className="flex items-center gap-1 mt-1 text-xs text-foreground-500 font-label">
                <i className="ri-calendar-line text-xs" />
                {entry.type === 'global' && (
                  <span>{(entry as GlobalEvent).date} {t('common.of')} every year — {t('dashboard.recurring')}</span>
                )}
                {entry.type === 'holiday' && (
                  <span>
                    {new Date((entry as SchoolHoliday).startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {(entry as SchoolHoliday).startDate !== (entry as SchoolHoliday).endDate && (
                      <> — {new Date((entry as SchoolHoliday).endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                    )}
                  </span>
                )}
                {entry.type === 'task' && (
                  <span>
                    {new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {task?.dueTime && <span> {t('common.at')} {task.dueTime}</span>}
                  </span>
                )}
              </div>

              {/* Description / details */}
              {(entry.type === 'global' && (entry as GlobalEvent).description) && (
                <p className="mt-1.5 text-xs text-foreground-500 font-label line-clamp-2">
                  {(entry as GlobalEvent).description}
                </p>
              )}
              {(entry.type === 'holiday' && (entry as SchoolHoliday).description) && (
                <p className="mt-1.5 text-xs text-foreground-500 font-label line-clamp-2">
                  {(entry as SchoolHoliday).description}
                </p>
              )}
              {(entry.type === 'task' && task?.description) && (
                <p className="mt-1.5 text-xs text-foreground-500 font-label line-clamp-2">
                  {task.description}
                </p>
              )}
            </div>

            {/* Actions for tasks */}
            {isTaskEntry && task && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => onTaskToggle(task.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-green-600 hover:bg-green-50 transition-colors cursor-pointer"
                  title={task.completed ? t('dashboard.markIncomplete') : t('dashboard.markComplete')}
                >
                  <i className={`${task.completed ? 'ri-checkbox-circle-fill text-green-500' : 'ri-checkbox-blank-circle-line'} text-lg`} />
                </button>
                <button
                  onClick={() => onDeleteTask(task.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title={t('common.delete')}
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}